import { z } from "zod";

import { baseEntityFields } from "./base.js";

/**
 * Les deux natures d'un contact. Vocabulaire FERMÉ, verrouillé aussi dans les
 * Security Rules : contrairement au `type` libre des autres familles, un
 * contact est soit une personne, soit une organisation (entreprise,
 * administration, association). La distinction fine (B2B/B2G, forme légale)
 * viendra par `customFields` le jour où une app en a besoin.
 */
export const CONTACT_TYPES = ["person", "organization"] as const;

/** Nature d'un contact. */
export type ContactType = (typeof CONTACT_TYPES)[number];

/**
 * Contact : entité métier cœur.
 *
 * Reflète EXACTEMENT le contrat partagé :
 *   Contact = { ...champs standard, type, name?, firstName?, lastName?,
 *               email?, phone?, orgId }
 *
 * Le SDK (@taki/sdk) valide les entrées avec ce schéma et remplit
 * automatiquement les champs standard (schemaVersion = 1) à la création.
 *
 * - `type`      : nature du contact, `person` ou `organization`. Défaut de
 *                 LECTURE `person` : les documents créés avant ce champ n'en
 *                 disposent pas et doivent rester lisibles (le script
 *                 `scripts/backfill-contact-type.mjs` pose la valeur en base,
 *                 sans quoi les filtres serveur `type == ...` les ignorent).
 * - `name`      : raison sociale d'une organisation. Optionnel dans le modèle,
 *                 exigé à la création d'une organisation par les Security Rules.
 * - `firstName` : prénom (une organisation n'en a pas).
 * - `lastName`  : nom (idem).
 * - `email`     : email, optionnel.
 * - `phone`     : téléphone, optionnel.
 * - `orgId`     : org de rattachement.
 */
export const ContactSchema = z.object({
  ...baseEntityFields,
  type: z.enum(CONTACT_TYPES).default("person"),
  name: z.string().min(1).optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string().email().optional(),
  phone: z.string().optional(),
  orgId: z.string(),
});

/** Type d'un contact. */
export type Contact = z.infer<typeof ContactSchema>;

/** La projection minimale dont les helpers d'affichage ont besoin. */
type ContactNameParts = Pick<
  Contact,
  "type" | "name" | "firstName" | "lastName" | "email"
>;

/**
 * Le nom à afficher d'un contact, écrit UNE fois pour toutes les apps.
 *
 * Chaque app qui concatène `firstName + lastName` à la main affiche une
 * organisation vide : ici la raison sociale prime pour une organisation, le
 * prénom-nom pour une personne, avec repli sur l'autre champ, puis l'email,
 * puis une mention neutre. Le résultat n'est jamais une chaîne vide.
 */
export function contactDisplayName(contact: ContactNameParts): string {
  const fullName = [contact.firstName, contact.lastName]
    .map((part) => part?.trim() ?? "")
    .filter((part) => part !== "")
    .join(" ");
  const legalName = contact.name?.trim() ?? "";
  const primary =
    contact.type === "organization"
      ? legalName || fullName
      : fullName || legalName;
  return primary || contact.email?.trim() || "Contact sans nom";
}

/**
 * Les initiales pour l'avatar, dérivées du nom affiché (jamais des seuls
 * `firstName`/`lastName`, absents d'une organisation) : premières lettres des
 * deux premiers mots, `?` si rien n'est affichable.
 */
export function contactInitials(contact: ContactNameParts): string {
  const displayName = contactDisplayName(contact);
  if (displayName === "Contact sans nom") return "?";
  const initials = displayName
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join("")
    .toUpperCase();
  return initials === "" ? "?" : initials;
}
